import type { JsonSchema7, VerticalLayout } from '@jsonforms/core';
import {
  addFieldToCustomAttributeSchema,
  removeField,
  removeFieldFromConditions,
  type TypedFormFieldConfiguration,
} from '@risksmart-app/form-configuration/src/field-service';
import {
  type Conditions,
  type CustomAttributeFieldType,
  isCustomAttributeFieldType,
  type Option,
} from '@risksmart-app/form-configuration/src/field-types/types';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type {
  FormEditorInitialData,
  FormEditorOutput,
} from 'src/blocks/form-editor-dialog';

import {
  canonicaliseStandardFieldKey,
  remapConditionPropertyKeys,
  scopeToServerId,
} from './conditionPropertyKeys';
import { convertEditorRulesToConditions } from './convertEditorStateToFormData';

/**
 * Converts the editor's `{ id, label }[]` option format to the
 * `Option[]` format expected by the orchestrator.
 */
const mapEditorOptionsToFieldOptions = (
  options?: { id: string; label: string }[]
): Option[] => {
  if (!options || options.length === 0) {
    return [];
  }

  return options.map((opt) =>
    opt.id !== opt.label
      ? { _tag: 'AltValueOption' as const, Value: opt.label, AltValue: opt.id }
      : { _tag: 'StringOption' as const, Value: opt.label }
  );
};

/**
 * Generates a unique attribute name for a new custom field.  Mirrors the
 * server-side format `${Date.now()}_${type}` but appends an index to avoid
 * collisions when multiple fields are created in the same millisecond
 * (the server is safe because each call is its own HTTP request, but a
 * client-side loop can collide).
 */
const generateAttributeName = (
  fieldType: CustomAttributeFieldType,
  index: number
): string => `${Date.now()}_${index}_${fieldType}`;

export interface FormConfigurationUpdatePayloadFieldConfiguration {
  FieldId: string;
  Required: boolean;
  Hidden: boolean;
  ReadOnly: boolean;
  Label: string | null;
  Description: string | null;
  Conditions: Conditions | null;
}

export interface FormConfigurationUpdatePayloadFieldOrdering {
  FieldId: string;
  Position: number;
}

export interface FormConfigurationUpdatePayloadSection {
  SectionId: string;
  Label: string;
  Description: string;
  Conditions: Conditions | null;
  Position: number;
  FieldIds: string[];
}

export interface FormConfigurationUpdatePayload {
  ParentType: FormId;
  CustomAttributeSchema: {
    Id: string;
    Schema: JsonSchema7;
    UiSchema: VerticalLayout;
  } | null;
  /**
   * Schema id to link from `form_configuration.CustomAttributeSchemaId`. Set
   * whenever a schema row exists for this form — including when the editor
   * is not changing the schema this save (in which case `CustomAttributeSchema`
   * is null because no upsert is needed, but the link still needs to be
   * preserved on the form_configuration row).
   */
  CustomAttributeSchemaId: string | null;
  FieldConfigurations: FormConfigurationUpdatePayloadFieldConfiguration[];
  FieldOrdering: FormConfigurationUpdatePayloadFieldOrdering[];
  Sections: FormConfigurationUpdatePayloadSection[];
  CustomFieldsToDelete: string[];
}

export interface BuildFormConfigurationUpdatePayloadArgs {
  output: FormEditorOutput;
  parentType: FormId;
  initialData: FormEditorInitialData;
  /**
   * The current custom attribute schema (or null if none exists).  Provides
   * the read-modify-write base for client-side schema computation.
   */
  customAttributeSchema: {
    Id: string;
    Schema: JsonSchema7;
    UiSchema: VerticalLayout;
  } | null;
  /**
   * The current form_field_configuration rows.  Used to recompute Conditions
   * on remaining fields when a referenced field is deleted (matches the
   * server-side orchestrator's behaviour in deleteFieldAndPersist).
   */
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
  /**
   * Lowercase React key → canonical FieldId. Used to translate standard-field
   * keys into the casing the form values use.
   */
  standardFieldCanonicalById: Map<string, string>;
}

/**
 * Pure transform that converts the form editor's output into the normalised
 * save payload sent to both the Apollo bulk mutation and the new tRPC
 * `updateFormConfiguration` endpoint.
 *
 * Schema deltas (custom field add / delete) are computed client-side using
 * the shared `@risksmart-app/form-configuration` orchestrator helpers, then
 * the resulting `Schema` / `UiSchema` plus all layout, ordering, mapping and
 * field-config writes are bundled into one normalised object.
 */
export const buildFormConfigurationUpdatePayload = ({
  output,
  parentType,
  initialData,
  customAttributeSchema,
  formFieldConfigurations,
  standardFieldCanonicalById,
}: BuildFormConfigurationUpdatePayloadArgs): FormConfigurationUpdatePayload => {
  const initialFieldIds = new Set(Object.keys(initialData.fields));
  const outputFieldIds = new Set(Object.keys(output.fields));

  // ---- 1. Compute schema delta + new-field IDs (in-memory) ----
  let schema: JsonSchema7 = customAttributeSchema?.Schema ?? {};
  let uiSchema: VerticalLayout = customAttributeSchema?.UiSchema ?? {
    type: 'VerticalLayout',
    elements: [],
  };

  // Custom fields removed in this save.  Stored in server format because
  // form_field_configuration uses CustomAttributeData.{path} ids.
  const customFieldsToDelete: string[] = [];

  for (const id of initialFieldIds) {
    if (!outputFieldIds.has(id) && initialData.fields[id].isCustom) {
      const path = scopeToServerId(id).slice('CustomAttributeData.'.length);
      ({ Schema: schema, UiSchema: uiSchema } = removeField(path, {
        Schema: schema,
        UiSchema: uiSchema,
      }));
      customFieldsToDelete.push(scopeToServerId(id));
    }
  }

  // Map of editor temp IDs -> real scope-format field IDs assigned
  // client-side.  Used to rewrite section layout references.
  const tempIdToRealId = new Map<string, string>();
  let newFieldIndex = 0;
  for (const id of outputFieldIds) {
    if (initialFieldIds.has(id)) {
      continue;
    }
    const f = output.fields[id];
    const rawType = f.config?.fieldType ?? f.type;
    if (!isCustomAttributeFieldType(rawType)) {
      throw new Error(`Invalid custom attribute field type: ${rawType}`);
    }
    const fieldType: CustomAttributeFieldType = rawType;
    const attributeName = generateAttributeName(fieldType, newFieldIndex++);
    ({ Schema: schema, UiSchema: uiSchema } = addFieldToCustomAttributeSchema({
      data: {
        IsCustomField: true,
        Label: f.config?.fieldName ?? f.name,
        Description: f.config?.guidanceEnabled
          ? (f.config.guidance ?? null)
          : null,
        Options: mapEditorOptionsToFieldOptions(f.config?.options),
      },
      attributeName,
      type: fieldType,
      customAttributeSchema: { Schema: schema, UiSchema: uiSchema },
    }));
    tempIdToRealId.set(id, `#/properties/${attributeName}`);
  }

  const resolveId = (id: string): string => tempIdToRealId.get(id) ?? id;

  // Translate every propertyKey from the editor's id form to the form-value
  // key. Save-time strategy: resolve freshly-created temp ids to their real
  // scope ids first, then convert scope → server format; standard fields
  // fall back to canonical casing.
  const remapConditionsPropertyKeys = (
    conditions: Conditions | null
  ): Conditions | null =>
    remapConditionPropertyKeys(conditions, (key) => {
      const resolved = resolveId(key);
      if (
        resolved !== key ||
        key.startsWith('CustomAttributeData.') ||
        key.startsWith('#/properties/')
      ) {
        return scopeToServerId(resolved);
      }

      return canonicaliseStandardFieldKey(key, standardFieldCanonicalById);
    });

  // ---- 2. Compute Conditions cleanup for remaining fields ----
  // When a referenced field is deleted, prune any token references in the
  // remaining fields' Conditions so saved logic doesn't dangle.  Mirrors
  // the orchestrator's deleteFieldAndPersist behaviour.
  const updatedConditionsByFieldId = new Map<
    string,
    TypedFormFieldConfiguration['Conditions']
  >();
  let remainingConfigs: TypedFormFieldConfiguration[] =
    formFieldConfigurations ?? [];
  for (const deletedFieldId of customFieldsToDelete) {
    const modified = removeFieldFromConditions({
      deletedFieldId,
      formFieldConfiguration: remainingConfigs,
    });
    for (const m of modified) {
      if (m.FieldId) {
        updatedConditionsByFieldId.set(m.FieldId, m.Conditions);
      }
    }
    // Apply the changes so subsequent deletions see the updated state.
    remainingConfigs = remainingConfigs.map((c) => {
      if (!c.FieldId) {
        return c;
      }
      const updated = modified.find((m) => m.FieldId === c.FieldId);

      return updated ? { ...c, Conditions: updated.Conditions ?? null } : c;
    });
  }

  // ---- 3. Assemble normalised payload ----
  const allFieldIds = output.sections.flatMap((s) => s.fieldIds.map(resolveId));

  // Build field configuration upserts for every field, including newly
  // inserted custom fields (whose form_field_configuration row no longer
  // exists from a separate action call — we create it here in the same
  // transaction).
  const fieldConfigurations: FormConfigurationUpdatePayloadFieldConfiguration[] =
    [...outputFieldIds].map((id) => {
      const f = output.fields[id];
      const fieldId = scopeToServerId(resolveId(id));
      // Editor-touched fields take precedence — the user already saw any
      // delete-cascade cleanup before saving. Untouched fields fall through
      // to the cascade override (already in server format), or null.
      const conditions: Conditions | null = f.config
        ? remapConditionsPropertyKeys(
            convertEditorRulesToConditions(
              f.config.conditionalLogicRules,
              f.config.conditionalLogicEnabled
            )
          )
        : (updatedConditionsByFieldId.get(fieldId) ?? null);

      return {
        FieldId: fieldId,
        Required: f.required,
        // Preserve existing Hidden state. Hardcoding false would silently
        // unhide any field an admin had previously hidden.
        Hidden: f.hidden ?? false,
        ReadOnly: f.readOnly ?? false,
        Label: f.name || null,
        Description: f.config?.guidanceEnabled
          ? (f.config.guidance ?? null)
          : null,
        Conditions: conditions,
      };
    });

  const fieldOrdering: FormConfigurationUpdatePayloadFieldOrdering[] =
    allFieldIds.map((fieldId, i) => ({
      FieldId: fieldId,
      Position: i,
    }));

  // Whether the schema needs to be touched at all.  If we're not adding
  // or removing any custom fields, leave custom_attribute_schema alone.
  const hasSchemaChanges =
    tempIdToRealId.size > 0 || customFieldsToDelete.length > 0;

  // Resolve the schema id: existing if there is one; otherwise generate a
  // new one when we have new custom fields (so we have something to link).
  const schemaId =
    customAttributeSchema?.Id ??
    (tempIdToRealId.size > 0 ? crypto.randomUUID() : null);

  const customAttributeSchemaPayload =
    hasSchemaChanges && schemaId
      ? { Id: schemaId, Schema: schema, UiSchema: uiSchema }
      : null;

  const sections: FormConfigurationUpdatePayloadSection[] = output.sections.map(
    (s, i) => ({
      SectionId: s.id,
      Label: s.name,
      Description: s.config?.description ?? '',
      Conditions: s.config
        ? remapConditionsPropertyKeys(
            convertEditorRulesToConditions(
              s.config.conditionalLogicRules,
              s.config.conditionalLogicEnabled
            )
          )
        : null,
      Position: i,
      FieldIds: s.fieldIds.map(resolveId),
    })
  );

  return {
    ParentType: parentType,
    CustomAttributeSchema: customAttributeSchemaPayload,
    CustomAttributeSchemaId: schemaId,
    FieldConfigurations: fieldConfigurations,
    FieldOrdering: fieldOrdering,
    Sections: sections,
    CustomFieldsToDelete: customFieldsToDelete,
  };
};
